import { useCallback, useMemo, useState } from 'react';
import type { AboTrancheRowExtendedType, AboTrancheRowType, IDocumentsSubscribedState, SortObjectType } from '../../types';
import { SortOrder } from '../../types';
import { decorateAboTranches, detailFilterBooleanVal, filterColumn, nullBottomSortFn } from './helpers';

const notSortableCols = ['all', 'kid', 'factsheet'];

export function useTableFiltering(entries: ReadonlyArray<AboTrancheRowType>, documentsSubscribed: IDocumentsSubscribedState, excludedColsInOtherDocs: string[]) {
  const [sort, setSort] = useState<SortObjectType>({ col: 'fundsname', order: SortOrder.DESC });
  const [liveFilterInfo, setLiveFilterInfo] = useState<Record<string, string[]>>({});
  const [detailFilterInfo, setDetailFilterInfo] = useState<Record<string, string[]>>({});
  const [subscriptionFilterInfo, setSubscriptionFilterInfo] = useState<Record<string, string[]>>({ subscribed: [''] });
  const [detailFilterVisible, setDetailFilterVisible] = useState(false);

  const handleSort = useCallback((sortCol: string): void => {
    if (notSortableCols.includes(sortCol)) {
      return;
    }
    setSort((prev) => ({
      col: sortCol,
      order: -prev.order as SortOrder,
    }));
  }, []);

  const liveFilterHandler = useCallback((cols: string[], p: { target: { value: string } }): void => {
    const info: Record<string, string[]> = {};
    for (const col of cols) {
      info[col] = [p.target.value];
    }
    setLiveFilterInfo(info);
  }, []);

  const detailFilterHandler = useCallback((col: string | null | undefined, filterVals: string[]): void => {
    if (col == null) {
      setDetailFilterInfo({});
      return;
    }

    let vals = filterVals;
    if (detailFilterBooleanVal.includes(col)) {
      vals = [...filterVals].splice(0, 1);
      setDetailFilterInfo((prev) => {
        if (prev[col] && prev[col][0] === vals[0]) {
          const next = { ...prev };
          delete next[col];
          return next;
        }
        return vals.length === 0
          ? (() => {
              const n = { ...prev };
              delete n[col];
              return n;
            })()
          : { ...prev, [col]: vals };
      });
      return;
    }

    setDetailFilterInfo((prev) => {
      if (vals.length === 0) {
        const next = { ...prev };
        delete next[col];
        return next;
      }
      return { ...prev, [col]: vals };
    });
  }, []);

  const subscriptionFilterHandler = useCallback((col: string | null | undefined, filterVals: string[]): void => {
    if (col == null) {
      setSubscriptionFilterInfo({ subscribed: [''] });
      return;
    }
    setSubscriptionFilterInfo((prev) => ({ ...prev, [col]: filterVals }));
  }, []);

  const toggleDetailFilter = useCallback((): void => {
    setDetailFilterVisible((prev) => !prev);
  }, []);

  const filteredResults = useMemo((): { results: AboTrancheRowExtendedType[]; count: number } => {
    const decorated = decorateAboTranches(entries as AboTrancheRowType[], documentsSubscribed as unknown as Record<string, Record<string, unknown>>, excludedColsInOtherDocs);

    const filtered = decorated.filter((tranche) => {
      if (!filterColumn(liveFilterInfo, tranche as unknown as Record<string, unknown>, true)) {
        return false;
      }
      if (!filterColumn(subscriptionFilterInfo, tranche as unknown as Record<string, unknown>, false)) {
        return false;
      }
      return filterColumn(detailFilterInfo, tranche as unknown as Record<string, unknown>, false);
    });

    const sorted = [...filtered].sort((a, b) => nullBottomSortFn(sort.col, sort.order, a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>));

    return { results: sorted, count: filtered.length };
  }, [entries, documentsSubscribed, excludedColsInOtherDocs, liveFilterInfo, subscriptionFilterInfo, detailFilterInfo, sort]);

  const sortInfo = useMemo(
    () => ({
      sortCol: sort.order,
      subsortCol: null,
    }),
    [sort.order],
  );

  return {
    sort,
    sortInfo,
    liveFilterInfo,
    detailFilterInfo,
    subscriptionFilterInfo,
    detailFilterVisible,
    filteredResults,
    handleSort,
    liveFilterHandler,
    detailFilterHandler,
    subscriptionFilterHandler,
    toggleDetailFilter,
  };
}
